import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Minus, Plus, X, Trash2, CreditCard, Utensils, MapPin, Clock } from "lucide-react";
import { fetchMenu, createOrder, updatePaymentStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrderReceipt } from "@/components/OrderReceipt";

const SPOKE_LOCATIONS = ["Aylesbury", "Whitehouse", "Bletchley", "Glebefarm", "Hatfield", "Uxbridge"];
const DEFAULT_SPOKE = "Bletchley";

const fmt = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;
const pad = (n) => String(n).padStart(2, "0");
const nowLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};
const mealFromClock = () => {
  const h = new Date().getHours();
  // Lunch until 15:00, then dinner. Matches ops brief (Lunch 12:45 / Dinner 16:30).
  return h < 15 ? "lunch" : "dinner";
};

// Category → color for the tile borders. Matches the brand palette used elsewhere.
const CAT_ACCENT = {
  "Biryani's":      "#DF513B",
  "Dessert's":      "#E8B241",
  "Buckets":        "#2D5C8F",
  "Starter's":      "#4F7355",
  "Combo's":        "#7E3C8A",
  "Chef Specials":  "#1A1714",
  "Drinks":         "#0E8A8A",
};

const SPOKE_KEY = "dotr_eftpos_spoke";

const MealPill = ({ meal }) => (
  <span
    data-testid="eftpos-meal-pill"
    className="inline-flex items-center gap-1 px-2 py-1 border-2 border-dotr-ink bg-dotr-yellow font-mono text-[10px] uppercase tracking-widest"
  >
    <Utensils size={11} />
    {meal === "lunch" ? "Lunch · 12:45" : "Dinner · 16:30"}
  </span>
);

const NowPill = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 border-2 border-dotr-ink bg-white font-mono text-[10px] uppercase tracking-widest" data-testid="eftpos-now-pill">
      <Clock size={11} />
      {now.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
    </span>
  );
};

const ItemTile = ({ item, onAdd, qty, sym }) => {
  const accent = CAT_ACCENT[item.category] || "#1A1714";
  const inCart = qty > 0;
  return (
    <button
      type="button"
      onClick={() => onAdd(item)}
      data-testid={`eftpos-tile-${item.name}`}
      className={`relative text-left border-2 border-dotr-ink bg-white p-3 md:p-4 transition-all group ${
        inCart ? "shadow-brutalSm translate-x-[1px] translate-y-[1px]" : "hover:shadow-brutalSm hover:-translate-x-px hover:-translate-y-px"
      }`}
      style={{ borderLeft: `10px solid ${accent}` }}
    >
      {inCart && (
        <span
          className="absolute -top-2 -right-2 min-w-[24px] h-6 px-1 flex items-center justify-center border-2 border-dotr-ink bg-dotr-primary text-white font-heading text-xs"
          data-testid={`eftpos-tile-qty-${item.name}`}
        >
          ×{qty}
        </span>
      )}
      <div className="font-heading font-semibold text-[15px] leading-tight uppercase tracking-tight break-words min-h-[38px]">
        {item.name}
      </div>
      <div className="mt-2 font-mono text-sm text-dotr-ink font-bold">
        {fmt(sym, item.price)}
      </div>
      {item.upcoming_price != null && item.upcoming_from && (
        <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-dotr-inkmuted">
          → {fmt(sym, item.upcoming_price)} from {item.upcoming_from}
        </div>
      )}
    </button>
  );
};

const CategorySection = ({ category, items, onAdd, cart, sym }) => (
  <div className="brutal-card overflow-hidden mb-5" data-testid={`eftpos-cat-${category}`}>
    <div className="flex items-center justify-between border-b-2 border-dotr-ink bg-dotr-ink text-white px-4 py-2.5">
      <span className="font-heading font-semibold text-sm uppercase tracking-[0.14em]">{category}</span>
      <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">{items.length} items</span>
    </div>
    <div className="p-3 md:p-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
      {items.map((it) => (
        <ItemTile
          key={it.name}
          item={{ ...it, category }}
          onAdd={onAdd}
          qty={cart[it.name] || 0}
          sym={sym}
        />
      ))}
    </div>
  </div>
);

export const EftposScreen = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [menu, setMenu] = useState([]);
  const [sym, setSym] = useState("£");
  const [priceMap, setPriceMap] = useState({});
  const [cart, setCart] = useState({}); // { itemName: qty }
  const [charging, setCharging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [spoke, setSpoke] = useState(() => {
    if (user?.role === "admin") {
      return localStorage.getItem(SPOKE_KEY) || DEFAULT_SPOKE;
    }
    return user?.spoke || "";
  });
  const [meal, setMeal] = useState(mealFromClock());
  const [lastReceipt, setLastReceipt] = useState(null);

  // Keep meal in sync with time (refresh every minute).
  useEffect(() => {
    const id = setInterval(() => setMeal(mealFromClock()), 60000);
    return () => clearInterval(id);
  }, []);

  // Persist admin's chosen spoke.
  useEffect(() => {
    if (isAdmin && spoke) localStorage.setItem(SPOKE_KEY, spoke);
  }, [isAdmin, spoke]);

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMenu();
      setMenu(res.menu || []);
      setSym(res.currency_symbol || "£");
      const pm = {};
      (res.menu || []).forEach((c) => c.items.forEach((it) => { pm[it.name] = it.price; }));
      setPriceMap(pm);
    } catch (e) {
      toast.error("Failed to load menu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  const add = (item) => setCart((c) => ({ ...c, [item.name]: (c[item.name] || 0) + 1 }));
  const dec = (name) => setCart((c) => {
    const next = { ...c };
    const q = (next[name] || 0) - 1;
    if (q <= 0) delete next[name];
    else next[name] = q;
    return next;
  });
  const removeLine = (name) => setCart((c) => {
    const next = { ...c };
    delete next[name];
    return next;
  });
  const clearCart = () => setCart({});

  const lineItems = useMemo(
    () => Object.entries(cart).map(([name, qty]) => ({ name, qty, price: priceMap[name] || 0, line: (priceMap[name] || 0) * qty })),
    [cart, priceMap]
  );
  const totalQty = lineItems.reduce((s, l) => s + l.qty, 0);
  const total = lineItems.reduce((s, l) => s + l.line, 0);

  const charge = async () => {
    if (!spoke) return toast.error("Pick a spoke location first.");
    if (lineItems.length === 0) return toast.error("Cart is empty — tap a menu tile to add items.");

    setCharging(true);
    try {
      const payload = {
        customer_name: "Walk-in",
        email: null,
        phone: null,
        pickup_delivery: "Pickup",
        spoke_location: spoke,
        lunch: meal === "lunch",
        dinner: meal === "dinner",
        requested_at: nowLocalISO(),
        special_instructions: "Eftpos walk-in",
        items: lineItems.map((l) => ({ category: "", name: l.name, quantity: l.qty })),
      };
      const created = await createOrder(payload);
      // Mark as paid — Eftpos = card payment settled at the terminal.
      try {
        const paid = await updatePaymentStatus(created.id, "Paid");
        setLastReceipt(paid);
      } catch {
        // Order was created but payment status update failed — still show receipt.
        setLastReceipt(created);
        toast.warning("Order captured but couldn't mark as Paid — please update manually.");
      }
      toast.success(`Charged ${fmt(sym, total)} · ${totalQty} item${totalQty === 1 ? "" : "s"}`);
      clearCart();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed.");
    } finally {
      setCharging(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)] gap-5 lg:gap-6" data-testid="eftpos-screen">
      {/* Menu grid */}
      <div>
        <div className="brutal-card p-4 md:p-5 mb-5" data-testid="eftpos-context-bar">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted flex items-center gap-1.5">
                <CreditCard size={12} /> Eftpos · Walk-in Capture
              </div>
              <div className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight">
                Tap to Charge
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isAdmin ? (
                <label className="inline-flex items-center gap-2 border-2 border-dotr-ink px-2 py-1 bg-white">
                  <MapPin size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">Spoke</span>
                  <select
                    value={spoke}
                    onChange={(e) => setSpoke(e.target.value)}
                    className="font-heading font-semibold text-sm uppercase tracking-tight bg-transparent focus:outline-none"
                    data-testid="eftpos-spoke-select"
                  >
                    {SPOKE_LOCATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 border-2 border-dotr-ink bg-white font-mono text-[10px] uppercase tracking-widest"
                  data-testid="eftpos-spoke-lock"
                >
                  <MapPin size={11} /> {spoke || "—"}
                </span>
              )}
              <MealPill meal={meal} />
              <NowPill />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="brutal-card p-16 flex items-center justify-center" data-testid="eftpos-loading">
            <Loader2 size={26} className="animate-spin" />
          </div>
        ) : (
          menu.map((cat) => (
            <CategorySection
              key={cat.category}
              category={cat.category}
              items={cat.items}
              onAdd={add}
              cart={cart}
              sym={sym}
            />
          ))
        )}
      </div>

      {/* Cart panel — sticky on desktop */}
      <aside className="brutal-card p-4 md:p-5 lg:sticky lg:top-4 lg:self-start" data-testid="eftpos-cart">
        <div className="flex items-center justify-between border-b-2 border-dotr-ink pb-3 mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">Order</div>
            <div className="font-heading font-semibold text-xl uppercase tracking-tight">
              {totalQty === 0 ? "Empty" : `${totalQty} item${totalQty === 1 ? "" : "s"}`}
            </div>
          </div>
          {lineItems.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              data-testid="eftpos-clear-cart"
              className="brutal-btn-ghost inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px]"
              aria-label="Clear cart"
            >
              <Trash2 size={12} /> Clear
            </button>
          )}
        </div>

        {lineItems.length === 0 ? (
          <div className="py-10 text-center" data-testid="eftpos-cart-empty">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
              Tap a menu tile to start
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="eftpos-cart-lines">
            {lineItems.map((l) => (
              <div
                key={l.name}
                className="grid grid-cols-[1fr_auto] gap-2 border-b border-dashed border-dotr-ink/30 pb-2"
                data-testid={`eftpos-line-${l.name}`}
              >
                <div className="min-w-0">
                  <div className="font-body text-sm truncate">{l.name}</div>
                  <div className="font-mono text-[11px] text-dotr-inkmuted">
                    {l.qty} × {fmt(sym, l.price)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => dec(l.name)}
                    data-testid={`eftpos-dec-${l.name}`}
                    className="w-7 h-7 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center"
                    aria-label={`Decrease ${l.name}`}
                  >
                    <Minus size={12} />
                  </button>
                  <span className="w-7 text-center font-heading text-sm font-semibold">{l.qty}</span>
                  <button
                    type="button"
                    onClick={() => add({ name: l.name })}
                    data-testid={`eftpos-inc-${l.name}`}
                    className="w-7 h-7 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center"
                    aria-label={`Increase ${l.name}`}
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeLine(l.name)}
                    data-testid={`eftpos-remove-${l.name}`}
                    className="w-7 h-7 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white flex items-center justify-center"
                    aria-label={`Remove ${l.name}`}
                  >
                    <X size={12} />
                  </button>
                </div>
                <div className="col-span-2 text-right font-mono text-sm font-bold">
                  {fmt(sym, l.line)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t-2 border-dotr-ink mt-3 pt-3">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">Total</span>
            <span
              className="font-heading font-semibold text-3xl text-dotr-primary tracking-tighter"
              data-testid="eftpos-total"
            >
              {fmt(sym, total)}
            </span>
          </div>
          <button
            type="button"
            onClick={charge}
            disabled={charging || lineItems.length === 0 || !spoke}
            data-testid="eftpos-charge-btn"
            className="brutal-btn w-full py-4 text-base inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {charging ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {charging ? "Charging…" : `Charge ${fmt(sym, total)}`}
          </button>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted leading-relaxed">
            Auto-fills: customer <b>Walk-in</b> · pickup · {spoke || "—"} · {meal} · payment <b>Paid</b>
          </div>
        </div>
      </aside>

      {lastReceipt && (
        <OrderReceipt order={lastReceipt} onClose={() => setLastReceipt(null)} />
      )}
    </div>
  );
};

export default EftposScreen;
